import React, { useRef, useEffect, useCallback, useState} from 'react';
import {
    SOUNDS} from 'constants/sidebar-view-modes.js';
import {useScroll} from 'components/v2/useScroll.js';
import {setSidebarSearchInput} from 'actions/index.js';
import {
    setContextualHelp,
    setSelectorMode,
    setSidebarViewMode
} from 'actions/index.js';
import {SIDEBAR_SEARCH} from 'constants/contextual-help.js';
import Documented from 'components/help/Documented.js';
import EffectChoices from './EffectChoices.js';
import {DEFAULT_TAGS} from './constants.js';
import {useSearcher} from './useSearcher.js';
import SidebarOptions from './SidebarOptions.js';
import {QUICK_ADD_BUTTON, QUICK_ADD_INSTRUMENT_BUTTON} from 'constants/contextual-help.js';
import {
    setSelectedPianoRollSteps,
    setArrangementBottomMode,
    setShowPianoRoll,
    setQuickAddSearchText,
    setSampleTagDragging
} from 'actions/index.js';
import {INFO} from 'constants/arrangement-modes.js';
import {connect} from 'react-redux';
import '../../QuickAdd.scss';


const QuickSampleChooser = (props) => { 
    const ref = useRef(undefined);
    const [selectedTag, setSelectedTag] = useState(undefined);
    const [selectedEffect, setSelectedEffect] = useState(undefined);
    const scrollTags = useRef(undefined);
    const [tags, setTags] = useState(DEFAULT_TAGS);
    const [effects, setEffects] = useState({});
    const {onChange, clear} = useSearcher({
        setTags,
        setEffects,
        tags,
        effects,
        sequencerEngine: props.sequencerEngine,
        searchText: props.searchText,
        setSearchText: props.setSearchText,
        selectedTag,
        setSelectedTag,
        selectedEffect,
        setSelectedEffect,
        ref
    });

    useEffect(() => {
        props.setSidebarSearchInput(ref);
    }, []);

    const showBottom = () => {
        props.setArrangementBottomMode(INFO);
        props.setShowPianoRoll(true);
    };

    const addSynth = useCallback(() => {
        props.sequencerEngine.addMultiSynthTrack();
        props.sequencerEngine.selectTrack(props.sequencerEngine.tracks.length - 1);
        // showBottom();
    }, [props.sequencerEngine]);

    const addRack = useCallback(() => {
        props.sequencerEngine.addInstrumentRackTrack();
        props.sequencerEngine.selectTrack(props.sequencerEngine.tracks.length - 1);
        // showBottom();
    }, [props.sequencerEngine]);


    const addInstrument = useCallback(() => {
        props.sequencerEngine.addSampledInstrumentTrack();
        props.sequencerEngine.selectTrack(props.sequencerEngine.tracks.length - 1);
        // showBottom();
    }, [props.sequencerEngine]);

    const openSelector = useCallback((tag) => {
        if (tag) {
            props.setSearchText(tag);
        }
        props.setSelectorMode(true);
        props.setSidebarViewMode("SOUNDS");
    }, []);

    let sampleClassName = "quick-sample-chooser";
    let instClassName = "quick-instrument-chooser";
    if (props.explorerMaximized || (props.selectedPianoRollSteps && props.selectedPianoRollSteps.filter(
        x => x && x.group).length > 0)) {
        sampleClassName += " hide";
        instClassName += " hide";
    }

    const onKeyDown = useCallback((e) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            openSelector();
        }
    }, []);
    const onMouseDown = useCallback(() => {
        props.setSelectedSteps(undefined);
    }, []);

    let quickClassName = "quick-actions";
    if (props.sequencerEngine.tracks.length === 0 && props.searchText !== "") {
        quickClassName += " no-tracks";
    }

    let tagsToShow = tags;
    let track = props.sequencerEngine.getSelectedTrack();
    if (props.searchText !== "" && track && track.instrument) {
        tagsToShow = {};
    }
    return (
        <div onMouseDown={onMouseDown} className={quickClassName}>
          <SidebarOptions/>
          <Documented element={SIDEBAR_SEARCH} className="search-container">
            <input
              ref={ref}
              onKeyDown={onKeyDown}
              className="quick-search"
              type="text"
              value={props.searchText}
              placeholder="Type to search"
              onChange={onChange}/>
            <div className="clear-btn" onClick={() => {
                if (props.searchText === "") {
                    if (props.hide) {
                        props.hide();
                    }
                } else {
                    clear();
                }
            }}>x</div>
          </Documented>
          <div
            ref={scrollTags}
            className={sampleClassName}>
            {Object.keys(tagsToShow).map(
                tag => 
                    <Tag
                      setSampleTagDragging={props.setSampleTagDragging}
                      key={tag}
                      scrollTags={scrollTags}
                      showBottom={showBottom}
                      openSelector={openSelector}
                      sequencerEngine={props.sequencerEngine}
                      isSelected={tag === selectedTag}
                      tag={tag}
                      tags={tags[tag]}
                    />)}
          </div>
          {Object.keys(effects).length > 0 ? 
           <EffectChoices selectedEffect={selectedEffect} effects={effects} sequencerEngine={props.sequencerEngine}/> : ''}
          {!props.searchText || props.searchText === "" ?
           <div className={instClassName}>
             <Documented element={QUICK_ADD_INSTRUMENT_BUTTON} arg="Synth">
               <div onClick={addSynth} className="instrument-choice">
                 Synth
               </div>
             </Documented>
             <Documented element={QUICK_ADD_INSTRUMENT_BUTTON} arg="Instrument">
               <div onClick={addInstrument} className="instrument-choice">
                 Instrument
               </div>
             </Documented>
             <Documented element={QUICK_ADD_INSTRUMENT_BUTTON} arg="Instrument Rack">
               <div onClick={addRack} className="instrument-choice">
                 Rack
               </div>
             </Documented>
           </div> : ""}
        </div>
    );
}

const Tag = ({openSelector, tag, scrollTags, tags, sequencerEngine, isSelected, setSampleTagDragging}) => {
    let ref = useRef(undefined);
    let [loading, setLoading] = useState(undefined);

    useScroll({
        scrollContainer: scrollTags,
        trackRef: ref,
        isSelected: isSelected,
        offset: 0,
        useClientBounds: true
    });

    const onClick = useCallback(async (e) => {
        setLoading(true);
        if (e.metaKey) {
            return openSelector(tag);
        }

        if (tag === '?') {
            // get a random sound 
            let c = sequencerEngine.ethereumCache;
            let fields = c.getAllForField('TAG', 0, 5000);
            let t = fields[Math.floor(Math.random() * fields.length)];
            sequencerEngine.searchForSounds([t.value]);
            return;
        }

        if (Array.isArray(tags)) {
            let r = Math.floor(Math.random() * tags.length);
            await sequencerEngine.searchForSounds(tags[r]);
        } else {
            await sequencerEngine.searchForSounds([tags]);
        }
        setLoading(false);
    }, [setLoading, tags]);

    const onDragStart = () => {
        if (Array.isArray(tags)) {
            let r = Math.floor(Math.random() * tags.length);
            setSampleTagDragging(tags[r]);
        } else {
            setSampleTagDragging([tags]);
        }
    };

    let className = isSelected ? "sample-chooser selected" : "sample-chooser";

    if (loading) {
        className += " flashit";
    }

    return (
        <Documented element={QUICK_ADD_BUTTON} arg={tag}>
          <div
            ref={ref}
            draggable={true}
            onDragOver={(e) => e.preventDefault()}
            onDragStart={onDragStart}
            onClick={onClick}
            className={className}>
            {tag}
          </div>
        </Documented>
    );
};

const mapStateToProps = (state, props) => {
  return {
      searchText: state.selector.quickAddSearchText,
      selectedPianoRollSteps: state.keyboard.selectedPianoRollSteps,
      track: props.sequencerEngine.getSelectedTrack(),
      explorerMaximized: state.selector.explorerMaximized
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
      setSearchText: x => dispatch(setQuickAddSearchText(x)),
      setShowPianoRoll: x => dispatch(setShowPianoRoll(x)),
      setArrangementBottomMode: x => dispatch(setArrangementBottomMode(x)),
      setSampleTagDragging: x => dispatch(setSampleTagDragging(x)),
      setSidebarSearchInput: (element) => dispatch(setSidebarSearchInput(element)),
      setSelectorMode: m => dispatch(setSelectorMode(m)),
      setSidebarViewMode: m => dispatch(setSidebarViewMode(m)),
      setSelectedSteps: m => dispatch(setSelectedPianoRollSteps(m)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(QuickSampleChooser);
